import { useNavigate, useParams } from 'react-router-dom'
import { formatPhoneNumber, refreshAccessToken } from '../utilities/extensions'
import { useEffect, useState } from 'react'
import { useTheme } from '../ThemeContext'
import { affiliateData, company, LeadWithContact } from '../data/AffiliateData'
import { apiURL } from '../utilities/URLs'
import { DateTime } from 'luxon'
import { addDays } from 'date-fns'

export default function Lead() {
  const navigate = useNavigate()
  const { leadId } = useParams()
  const { isDarkMode } = useTheme()

  const [fetchingLead, setFetchingLead] = useState(true)
  const [lead, setLead] = useState(new LeadWithContact())
  const [filingTimer, setFilingTimer] = useState({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
  })

  const subtractBusinessDays = (date, days) => {
    let currentDate = new Date(date)
    let remainingDays = days
  
    while (remainingDays > 0) {
      currentDate = addDays(currentDate, -1)
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        remainingDays -= 1
      }
    }
  
    return currentDate
  }

  useEffect(() => {
    const handleGetLead = async () => {
      const leadDataResponse = await fetch(`${apiURL}/api/getLead?leadId=${encodeURIComponent(leadId)}`, {
        headers: {
          'Authorization': affiliateData.accessToken
        }
      })
      const leadData = await leadDataResponse.json()
      if(leadData.response.error) {
        if(leadData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? handleGetLead() : navigate('/affiliate', { state: { expired: true } })
        } else
          console.error(leadData.response.error)
      } else {
        setLead(new LeadWithContact(leadData.response.lead))
        setFetchingLead(false)
      }
    }

    handleGetLead()
    document.title = `${company.companyName} | Deal Details`
  }, [])

  useEffect(() => {
    const filingDate = lead.filingDate || '2022-04-18'
    const filingDateNY = DateTime.fromISO(filingDate, { zone: 'America/New_York' })
    const hasExtension = filingDateNY > DateTime.fromISO('2022-04-18', { zone: 'America/New_York' })
  
    let [year, month, day] = ['2022', '04', '18']
    if (/^\d{4}-\d{2}-\d{2}$/.test(filingDate)) {
      [year, month, day] = filingDate.split('-')
    }
  
    const timerMonth = hasExtension ? month : '04'
    const timerDay = hasExtension ? day : '18'
  
    const currentYear = DateTime.now().setZone('America/New_York').year
    const rawTargetDate = DateTime.fromObject({
      year: currentYear,
      month: parseInt(timerMonth),
      day: parseInt(timerDay),
      hour: 23,
      minute: 59,
      second: 59,
    }, { zone: 'America/New_York' }).toMillis()
  
    const maxDate = DateTime.fromISO('2025-10-15T23:59:59', { zone: 'America/New_York' }).toMillis()
    let targetDate = year !== '2022' ? maxDate : Math.min(rawTargetDate, maxDate)
  
    if (lead.leadStageName.startsWith('AF')) {
      const adjustedTargetDate = subtractBusinessDays(targetDate, hasExtension ? 1 : 0)
      targetDate = adjustedTargetDate.getTime()
    }

    let interval
  
    const updateCountdown = () => {
      const now = DateTime.now().setZone('America/New_York').toMillis()
      const difference = targetDate - now
  
      if (difference <= 0) {
        setFilingTimer({
          days: '00',
          hours: '00',
          minutes: '00',
          seconds: '00',
        })
        clearInterval(interval)
        return
      }
  
      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((difference % (1000 * 60)) / 1000)
  
      setFilingTimer({
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0'),
      })
    }
  
    updateCountdown()
    interval = setInterval(updateCountdown, 1000)
  
    return () => clearInterval(interval)
  }, [lead.filingDate])

  return (
    <div className='leadDetails flex flex-col items-center font-mont'>
      <h1 className={`leadDetailsHeader text-2xl font-bold mt-10 ${isDarkMode && 'text-white'}`}>Lead Details</h1>
      {fetchingLead ? (
         <div className='flex flex-row items-center justify-center gap-2 mt-5 w-full col-span-2'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8'></div>
          <p className={`text-md text-center ${isDarkMode && 'text-white'}`}>Fetching Lead Info...</p>
        </div>
      ) : (
        <div className='grid grid-cols-2 max-sm:grid-cols-1 lg:w-4/5'>
          <section className={`leadInfo mt-8 p-6 w-full ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            <div className='flex flex-row items-center justify-between border-b-2 border-gray-300 mb-6'>
              <h2 className='text-2xl font-semibold pb-2'>Lead Information</h2>
            </div>
            <div className='w-full flex flex-col items-center gap-4'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 w-full'>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Name</span>
                  <span className='text-lg font-semibold'>{lead.contactName || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Company</span>
                  <span className='text-lg font-semibold'>{lead.companyName || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Stage</span>
                  <span className='text-lg font-semibold'>{lead.leadStageName || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Source</span>
                  <span className='text-lg font-semibold'>{lead.leadSource || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Type</span>
                  <span className='text-lg font-semibold'>{lead.leadType || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Amount</span>
                  <span className='text-lg font-semibold'>${lead.leadAmount.toLocaleString(undefined, { minimumFractionDigits: 2 }) || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Created At</span>
                  <span className='text-lg font-semibold'>{lead.leadCreatedAt ? lead.leadCreatedAt.toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Modified At</span>
                  <span className='text-lg font-semibold'>{lead.leadModifiedAt ? lead.leadModifiedAt.toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </div>
          </section>
          <section className={`leadInfo mt-8 p-6 w-full ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            <div className='flex flex-row items-center justify-between border-b-2 border-gray-300 mb-6'>
              <h2 className='text-2xl font-semibold pb-2'>Contact Information</h2>
            </div>
            <div className='w-full flex flex-col items-center gap-4'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 w-full'>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>First Name</span>
                  <span className='text-lg font-semibold'>{lead.contactFirstName || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Last Name</span>
                  <span className='text-lg font-semibold'>{lead.contactLastName || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Email</span>
                  <span className='text-lg font-semibold'>{lead.contactEmail || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Phone</span>
                  <span className='text-lg font-semibold'>{formatPhoneNumber(lead.contactPhone) || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Last 4 SSN</span>
                  <span className='text-lg font-semibold'>{lead.contactLast4SSN || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Birth Year</span>
                  <span className='text-lg font-semibold'>{lead.contactBirthYear || 'N/A'}</span>
                </div>
              </div>
            </div>
          </section>
          <section className={`leadInfo mt-8 p-6 w-full ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              <div className='w-full flex flex-row items-center justify-between border-b-2 border-gray-300 mb-6'>
                <h2 className='text-2xl font-semibold pb-2'>Customer Tax Information</h2>
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 w-full'>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Tax Filing Date</span>
                  <span className='text-lg font-semibold'>{lead.filingDate || 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>Remaining Time</span>
                  <span className='text-lg font-semibold'>{lead.filingDate ? filingTimer.days+':'+filingTimer.hours+':'+filingTimer.minutes+':'+filingTimer.seconds : 'N/A'}</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-gray-500'>IRS Balance</span>
                  <span className='text-lg font-semibold'>${lead.leadIRSBalance.toLocaleString(undefined, { minimumFractionDigits: 2 }) || 'N/A'}</span>
                </div>
              </div>
            </section>
        </div>
      )}
    </div>
  )
}